import axios from 'axios';

const API_URL = 'https://api.caninesunleashedinc.com/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add interceptor to add token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const login = async (accessPin, userName) => {
  try {
    const response = await api.post('/account/login', {
      access_pin: accessPin,
      user_name: userName,
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const getPreferences = async (roomId) => {
  try {
    const response = await api.get('/preferences/', {
      params: { room_id: roomId }
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const savePreferences = async (roomId, preferences) => {
  try {
    const response = await api.post('/preferences/', {
      room_id: roomId,
      ...preferences
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const uploadAudio = async (roomId, audioBlob) => {
  const formData = new FormData();
  formData.append('file', audioBlob, 'voice_message.webm');
  formData.append('room_id', roomId);

  try {
    const response = await api.post('/audio/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const getAudioList = async (roomId) => {
  try {
    const response = await api.get(`/audio/list?room_id=${roomId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const scheduleAudio = async (messageId, roomId, playOn) => {
  try {
    const response = await api.post('/audio/schedule', {
      message_id: messageId,
      room_id: roomId,
      play_on: playOn
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const deleteAudio = async (messageId) => {
  try {
    const response = await api.delete(`/audio/${messageId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const getAudio = async (messageId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/v1/audio/${messageId}/audio_file`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to play audio');
    }
    return response;
  } catch (error) {
    throw error;
  }
};

export const getChatMessages = async (roomId) => {
  try {
    const response = await api.get('/chat/', {
      params: { room_id: roomId }
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const sendChatMessage = async (roomId, message) => {
  try {
    const response = await api.post('/chat/', {
      room_id: roomId,
      message: message
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const getUnreadCount = async (roomId, lastReadTime) => {
  try {
    const params = { room_id: roomId };
    if (lastReadTime) {
      params.last_read = lastReadTime;
    }
    const response = await api.get('/chat/unread', { params });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const requestBone = async (roomId) => {
  try {
    const response = await api.post('/controls/bone', { room_id: roomId });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const requestTreat = async (roomId) => {
  try {
    const response = await api.post('/controls/treat', { room_id: roomId });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const requestTV = async (roomId, action, duration) => {
  try {
    const response = await api.post('/controls/tv', {
      room_id: roomId,
      action,        // 'on' | 'off'
      duration,      // duration in minutes when switching on; ignored when switching off
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const getTVStatus = async (roomId) => {
  try {
    const response = await api.get('/controls/tv/status', {
      params: { room_id: roomId }
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

// Returns true if the relay is ON, false otherwise.
export const getRelayStatus = async (roomId) => {
  try {
    const response = await api.get('/controls/relay/status', {
      params: { room_id: roomId }
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const requestRelay = async (roomId, action, duration) => {
  try {
    const response = await api.post('/controls/relay', {
      room_id: roomId,
      action,        // 'on' | 'off'
      duration,      // duration in minutes when switching on; ignored when switching off
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const getProducts = async () => {
  try {
    const response = await api.get('/products/');
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const submitOrder = async (items) => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const roomId = user.rooms === '*' ? '101' : user.rooms?.split(',')[0];
    
    // Support both new format (array of items) and legacy format (pipe-separated string)
    const payload = {
      room_id: roomId
    };
    
    if (Array.isArray(items)) {
      // New format: array of items with quantities
      payload.items = items;
    } else {
      // Legacy format: pipe-separated SKUs
      payload.skus = items;
    }
    
    const response = await api.post('/products/order', payload);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const getUserOrders = async (roomId, since) => {
  try {
    const response = await api.get('/products/orders', {
      params: { room_id: roomId, since }
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export const getRoomPort = async (roomId) =>{
    try{
      var response = await api.get(`/room/${roomId}/port`);
      return response.data.dispenser_port;
    }catch (error) {
      throw error.response ? error.response.data : new Error('Network error');
    }
};
export const getCommonCameras = async (roomId) => {
  try {
    const response = await api.get(`/room/list_common_camera_urls`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

export default api;
